import React, { useState, useRef, useEffect } from 'react';
import { PaperAirplaneIcon, SparklesIcon } from '@heroicons/react/24/solid';
import api from '../api/client';

/**
 * ChatInterface
 *
 * Props:
 *   onIntentDetected(intent, metadata): Called when an assistant response arrives.
 *     - intent: string (e.g., 'risk_assessment', 'compliance_status')
 *     - metadata: full response object from the API
 *   The parent (Dashboard) uses this to drive the right-hand context panel.
 */
export default function ChatInterface({ onIntentDetected }) {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content:
        "Hello! I'm your AI-powered CISO. I can help you assess security risks, analyze compliance gaps, and provide recommendations. What would you like to know?",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const conversationId = useRef(`conv_${Date.now()}`);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const quickActions = [
    'Assess our AWS security posture',
    'What are our top security risks?',
    'Generate compliance report',
    'Calculate risk for our infrastructure',
  ];

  const handleSend = async (messageText = input) => {
    if (!messageText.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await api.processQuery(messageText, conversationId.current);

      const assistantMessage = {
        role: 'assistant',
        content: response.response || response.result || 'Request processed successfully.',
        timestamp: new Date(),
        metadata: response,
      };

      setMessages((prev) => [...prev, assistantMessage]);

      // Notify the parent of the detected intent so the context panel
      // can update. We accept either a top-level `intent` field or
      // a nested `classification.primary_type` (depending on which
      // backend code path responded).
      if (onIntentDetected) {
        const detectedIntent =
          response.intent ||
          response.classification?.primary_type ||
          null;

        if (detectedIntent) {
          onIntentDetected(detectedIntent, response);
        }
      }
    } catch (error) {
      const errorMessage = {
        role: 'assistant',
        content: `Error: ${error.response?.data?.detail || error.message}`,
        timestamp: new Date(),
        error: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full p-4">
      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto bg-gray-800 rounded-lg p-4 mb-4 min-h-0">
        <div className="space-y-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-full rounded-lg px-4 py-3 ${
                  message.role === 'user'
                    ? 'bg-primary-600 text-white'
                    : message.error
                    ? 'bg-danger-900/50 border border-danger-500 text-danger-200'
                    : 'bg-gray-700 text-gray-100'
                }`}
              >
                {message.role === 'assistant' && !message.error && (
                  <div className="flex items-center space-x-2 mb-2">
                    <SparklesIcon className="h-4 w-4 text-primary-400" />
                    <span className="text-xs font-semibold text-primary-400">CISO Advisor</span>
                    {message.metadata?.intent && (
                      <span className="text-xs text-gray-400">
                        · {message.metadata.intent.replace(/_/g, ' ')}
                      </span>
                    )}
                  </div>
                )}

                <div className="whitespace-pre-wrap text-sm">{message.content}</div>

                {message.metadata?.workflow_id && (
                  <div className="mt-2 pt-2 border-t border-gray-600 text-xs text-gray-400">
                    Workflow ID: {message.metadata.workflow_id}
                  </div>
                )}

                <div className="text-xs text-gray-400 mt-2">
                  {message.timestamp.toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-gray-700 rounded-lg px-4 py-3">
                <div className="flex items-center space-x-2">
                  <div className="animate-pulse flex space-x-1">
                    <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                    <div className="w-2 h-2 bg-primary-400 rounded-full animation-delay-200"></div>
                    <div className="w-2 h-2 bg-primary-400 rounded-full animation-delay-400"></div>
                  </div>
                  <span className="text-sm text-gray-400">Analyzing...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Quick Actions - shown only on first turn */}
      {messages.length === 1 && (
        <div className="mb-4">
          <p className="text-xs text-gray-400 mb-2">Try asking:</p>
          <div className="grid grid-cols-1 gap-2">
            {quickActions.map((action, index) => (
              <button
                key={index}
                onClick={() => handleSend(action)}
                className="text-left px-3 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-xs text-gray-300 transition-colors"
              >
                {action}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input Box */}
      <div className="bg-gray-800 rounded-lg p-3 border border-gray-700 flex-shrink-0">
        <div className="flex space-x-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything about your security posture..."
            className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            disabled={loading}
          />
          <button
            onClick={() => handleSend()}
            disabled={!input.trim() || loading}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
          >
            <PaperAirplaneIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
